import { CssBaseline, ThemeProvider } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./app/AppShell";
import { useAppOutletContext } from "./app/outlet";
import { appTheme } from "./app/theme";
import { AuthPage } from "./features/auth/AuthPage";
import { GroupSetupPage } from "./features/groups/GroupSetupPage";
import { BankAccountsPage } from "./pages/BankAccountsPage";
import { CategoriesPage } from "./pages/CategoriesPage";
import { CurrenciesPage } from "./pages/CurrenciesPage";
import { FeedbackPage } from "./pages/FeedbackPage";
import { InviteRegisterPage } from "./pages/InviteRegisterPage";
import { LinkedTransactionsPage } from "./pages/LinkedTransactionsPage";
import { McpTokensPage } from "./pages/McpTokensPage";
import { OverviewPage } from "./pages/OverviewPage";
import { RecordsPage } from "./pages/RecordsPage";
import { RecurringPaymentsPage } from "./pages/RecurringPaymentsPage";
import { SettingsOverview } from "./pages/SettingsOverview";
import { StatisticsPage } from "./pages/StatisticsPage";
import { TagsPage } from "./pages/TagsPage";
import { UsersGroupPage } from "./pages/UsersGroupPage";
import "./styles.css";

// Debug and backoffice screens are code-split so regular users never download them.
const DebugPage = lazy(() =>
  import("./pages/DebugPage").then((module) => ({ default: module.DebugPage })),
);
const BackofficeLogin = lazy(() =>
  import("./backoffice/BackofficeLogin").then((module) => ({
    default: module.BackofficeLogin,
  })),
);
const BackofficeShell = lazy(() =>
  import("./backoffice/BackofficeShell").then((module) => ({
    default: module.BackofficeShell,
  })),
);
const BackofficeUsersPage = lazy(() =>
  import("./backoffice/BackofficeUsersPage").then((module) => ({
    default: module.BackofficeUsersPage,
  })),
);
const BackofficeCurrenciesPage = lazy(() =>
  import("./backoffice/BackofficeCurrenciesPage").then((module) => ({
    default: module.BackofficeCurrenciesPage,
  })),
);
const BackofficeImportInstructionsPage = lazy(() =>
  import("./backoffice/BackofficeImportInstructionsPage").then((module) => ({
    default: module.BackofficeImportInstructionsPage,
  })),
);
const BackofficeTipsPage = lazy(() =>
  import("./backoffice/BackofficeTipsPage").then((module) => ({
    default: module.BackofficeTipsPage,
  })),
);

const queryClient = new QueryClient();

function RecordsOutlet() {
  const outlet = useAppOutletContext();
  return (
    <RecordsPage
      group={outlet.group}
      onBlockedAccess={() => outlet.onBlockedGroupAccess(outlet.group.id)}
    />
  );
}

function OverviewOutlet() {
  const outlet = useAppOutletContext();
  return <OverviewPage group={outlet.group} user={outlet.user} />;
}

function StatisticsOutlet() {
  const outlet = useAppOutletContext();
  return (
    <StatisticsPage
      group={outlet.group}
      onBlockedAccess={() => outlet.onBlockedGroupAccess(outlet.group.id)}
    />
  );
}

function RecurringPaymentsOutlet() {
  const outlet = useAppOutletContext();
  return (
    <RecurringPaymentsPage
      group={outlet.group}
      onBlockedAccess={() => outlet.onBlockedGroupAccess(outlet.group.id)}
    />
  );
}

function CategoriesOutlet() {
  const outlet = useAppOutletContext();
  return (
    <CategoriesPage
      group={outlet.group}
      onBlockedAccess={() => outlet.onBlockedGroupAccess(outlet.group.id)}
    />
  );
}

function BankAccountsOutlet() {
  const outlet = useAppOutletContext();
  return (
    <BankAccountsPage
      group={outlet.group}
      onBlockedAccess={() => outlet.onBlockedGroupAccess(outlet.group.id)}
    />
  );
}

function CurrenciesOutlet() {
  const outlet = useAppOutletContext();
  return (
    <CurrenciesPage
      group={outlet.group}
      onBlockedAccess={() => outlet.onBlockedGroupAccess(outlet.group.id)}
    />
  );
}

function TagsOutlet() {
  const outlet = useAppOutletContext();
  return (
    <TagsPage
      group={outlet.group}
      onBlockedAccess={() => outlet.onBlockedGroupAccess(outlet.group.id)}
    />
  );
}

function UsersGroupOutlet() {
  const outlet = useAppOutletContext();
  return (
    <UsersGroupPage
      group={outlet.group}
      user={outlet.user}
      onBlockedAccess={() => outlet.onBlockedGroupAccess(outlet.group.id)}
    />
  );
}

function LinkedTransactionsOutlet() {
  const outlet = useAppOutletContext();
  return (
    <LinkedTransactionsPage
      group={outlet.group}
      onBlockedAccess={() => outlet.onBlockedGroupAccess(outlet.group.id)}
    />
  );
}

function McpTokensOutlet() {
  return <McpTokensPage />;
}

function DebugOutlet() {
  const outlet = useAppOutletContext();
  return (
    <DebugPage
      group={outlet.group}
      onBlockedAccess={() => outlet.onBlockedGroupAccess(outlet.group.id)}
    />
  );
}

function FeedbackOutlet() {
  const outlet = useAppOutletContext();
  return <FeedbackPage user={outlet.user} />;
}

function SettingsOverviewOutlet() {
  const outlet = useAppOutletContext();
  return <SettingsOverview group={outlet.group} user={outlet.user} />;
}

function Root() {
  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Suspense fallback={null}>
            <Routes>
              <Route index element={<AuthPage mode="login" />} />
              <Route path="register" element={<AuthPage mode="register" />} />
              <Route path="join/:code" element={<InviteRegisterPage />} />
              <Route path="groups/new" element={<GroupSetupPage />} />
              <Route element={<AppShell />}>
                <Route path="home" element={<OverviewOutlet />} />
                <Route path="records" element={<RecordsOutlet />} />
                <Route path="statistics" element={<StatisticsOutlet />} />
                <Route
                  path="recurring-payments"
                  element={<RecurringPaymentsOutlet />}
                />
                <Route
                  path="planned-payments"
                  element={<Navigate to="/recurring-payments" replace />}
                />
                <Route path="settings" element={<SettingsOverviewOutlet />} />
                <Route
                  path="settings/users-family"
                  element={<UsersGroupOutlet />}
                />
                <Route
                  path="settings/bank-accounts"
                  element={<BankAccountsOutlet />}
                />
                <Route
                  path="settings/currencies"
                  element={<CurrenciesOutlet />}
                />
                <Route
                  path="settings/categories"
                  element={<CategoriesOutlet />}
                />
                <Route path="settings/tags" element={<TagsOutlet />} />
                <Route
                  path="settings/linked-transactions"
                  element={<LinkedTransactionsOutlet />}
                />
                <Route path="settings/mcp" element={<McpTokensOutlet />} />
                <Route path="settings/feedback" element={<FeedbackOutlet />} />
                <Route path="settings/debug" element={<DebugOutlet />} />
              </Route>
              <Route path="backoffice">
                <Route
                  index
                  element={<Navigate to="/backoffice/users" replace />}
                />
                <Route path="login" element={<BackofficeLogin />} />
                <Route element={<BackofficeShell />}>
                  <Route path="users" element={<BackofficeUsersPage />} />
                  <Route
                    path="currencies"
                    element={<BackofficeCurrenciesPage />}
                  />
                  <Route
                    path="import-instructions"
                    element={<BackofficeImportInstructionsPage />}
                  />
                  <Route path="tips" element={<BackofficeTipsPage />} />
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
);
